import { useEffect, useState } from "react";
import { Megaphone } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { api } from "../../lib/api";
import PeriodSelect, { usePeriod } from "../PeriodSelect";

const eur = (v) => `€${Number(v || 0).toFixed(2)}`;
const METRICS = [["cpl", "CPL"], ["ctr", "CTR %"], ["cpm", "CPM"], ["cpc", "CPC"]];

export default function CampaignsPanel({ client = "", title = "Campagne Meta", subtitle = "" }) {
  const [data, setData] = useState(null);
  const [period, setPeriod] = usePeriod("last30", "athanor_campaigns_period");
  const [metric, setMetric] = useState("cpl");
  useEffect(() => { api.get("/meta/campaigns", { params: { start: period.start, end: period.end, client } }).then(({ data }) => setData(data)).catch(() => setData({ connected: false, campaigns: [], daily: [] })); }, [period.start, period.end, client]);
  if (!data || !data.connected) return null;
  const chart = data.daily.map((d) => ({ ...d, label: d.date.slice(5), cpl: d.cpl ?? null }));
  return (
    <div className="athanor-card p-5" data-testid={client ? `campaigns-panel-${client}` : "campaigns-panel"}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Megaphone className="h-4 w-4 text-[#C5A059]" />
          <div><h3 className="text-base font-semibold text-slate-100">{title}</h3>{subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}</div>
        </div>
        <div className="flex items-center gap-2">
          <select value={metric} onChange={(e) => setMetric(e.target.value)} data-testid="campaigns-metric" className="rounded-md border border-zinc-800 bg-zinc-900/50 px-2 py-1 text-xs text-slate-200">
            {METRICS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <PeriodSelect period={period} setPeriod={setPeriod} testid="campaigns-days" small />
        </div>
      </div>
      <div className="h-56" data-testid="campaigns-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chart} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#26262a" strokeDasharray="3 3" />
            <XAxis dataKey="label" tick={{ fill: "#8e8e93", fontSize: 12 }} />
            <YAxis yAxisId="l" tick={{ fill: "#8e8e93", fontSize: 12 }} />
            <YAxis yAxisId="r" orientation="right" tick={{ fill: "#e5e5ea", fontSize: 12 }} />
            <Tooltip contentStyle={{ background: "#0b0b0c", border: "1px solid #3f3f46", fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar yAxisId="l" dataKey="spend" name="Spesa €" fill="#3f3f46" radius={[2, 2, 0, 0]} maxBarSize={40} />
            <Bar yAxisId="l" dataKey="leads" name="Lead" fill="#e5e5ea" radius={[2, 2, 0, 0]} maxBarSize={40} />
            <Line yAxisId="r" type="monotone" dataKey={metric} name={METRICS.find(([v]) => v === metric)[1]} stroke="#64d2ff" strokeWidth={2} dot={false} connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-left text-sm" data-testid="campaigns-table">
          <thead className="text-xs text-slate-500">
            <tr><th className="py-1 pr-3">Campagna</th><th className="py-1 pr-3 text-right">Spesa</th><th className="py-1 pr-3 text-right">Lead</th><th className="py-1 pr-3 text-right">CPL</th><th className="py-1 pr-3 text-right">CTR</th><th className="py-1 pr-3 text-right">CPM</th><th className="py-1 text-right">CPC</th></tr>
          </thead>
          <tbody className="text-slate-200">
            {data.campaigns.map((c) => (
              <tr key={c.campaign_id} className="border-t border-zinc-800/60" data-testid={`campaign-row-${c.campaign_id}`}>
                <td className="py-1.5 pr-3 truncate max-w-[220px]">{c.name}</td>
                <td className="py-1.5 pr-3 text-right">{eur(c.spend)}</td>
                <td className="py-1.5 pr-3 text-right">{c.leads}</td>
                <td className="py-1.5 pr-3 text-right">{c.cpl != null ? eur(c.cpl) : "—"}</td>
                <td className="py-1.5 pr-3 text-right">{c.ctr}%</td>
                <td className="py-1.5 pr-3 text-right">{eur(c.cpm)}</td>
                <td className="py-1.5 text-right">{eur(c.cpc)}</td>
              </tr>
            ))}
            {data.campaigns.length === 0 && <tr><td colSpan={7} className="py-4 text-center text-slate-500">Nessun dato nel periodo.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
